import { useCallback } from 'react';
import { ICON_COLOR } from '@zep-us/zus/tokens';
import { BoltFillIcon, OrderIcon, TextLongLeftIcon } from '@zep-us/zus-icons';
import { motion } from 'framer-motion';
import { useAtomValue } from 'jotai';
import { useTranslation } from 'next-i18next';
import { logger } from '@zep/core-logger';
import { Tooltip } from '@/components/ui/Tooltip';
import { CheckSmallCircleIcon, MagicwandFillIcon, MessageLinesIcon, SquareGridSemiFillIcon } from '@/icons';
import { WithPlanBadge } from '@/shared/components/Badge/PlanBadge';
import { LocalizedOxTypeIcon } from '@/shared/components/LocalizedOxTypeIcon';
import { useUpsellingGuard, useUserPlanManager } from '@/shared/plan';
import { type QuizType } from '@/types/Quiz';
import { IconButton } from '@/ui_v3';
import { useAiBuilderChatAvailability, useOpenAiBuilderChat } from '@/views/build/features/ai-builder-chat/hooks';
import { useAiBuilderChatPanelSelector } from '@/views/build/features/ai-builder-chat/store';
import { editingQuizIdAtom } from './focus/editModeAtom';
import { useQuizList } from './useQuizList';
type FloatingAiEntryTransitionState = 'visible' | 'exiting';
export const BuildQuizFloatingButtons = ({
  aiEntryTransitionState = 'visible'
}: {
  aiEntryTransitionState?: FloatingAiEntryTransitionState;
}) => {
  const {
    t
  } = useTranslation(['build', 'common']);
  const {
    handleCreate,
    quizList
  } = useQuizList();
  const {
    panelOpen
  } = useAiBuilderChatPanelSelector();
  const {
    canUseAiBuilderChat
  } = useAiBuilderChatAvailability();
  const upsellingGuard = useUpsellingGuard();
  const {
    userPlanManager
  } = useUserPlanManager();
  const featurePlanType = userPlanManager.getFeaturePlanType('MATCH');
  const orderFeaturePlanType = userPlanManager.getFeaturePlanType('ORDER');
  const editingId = useAtomValue(editingQuizIdAtom);
  const handleCreateQuiz = useCallback((type: QuizType) => {
    logger.track('add_quizItem', {
      type,
      position: 'floating'
    });
    if (!editingId) {
      handleCreate(type);
      return;
    }
    const editIndex = quizList.findIndex(quiz => quiz.id === editingId);
    if (editIndex !== -1) {
      handleCreate(type, editIndex);
    }
  }, [editingId, handleCreate, quizList]);
  if (quizList.length === 0) return null;
  const shouldRenderAiEntry = canUseAiBuilderChat && (!panelOpen || aiEntryTransitionState === 'exiting');
  return <div className="border-black/8 fixed left-[calc(50%+336.5px)] top-[272px] z-30 hidden w-fit rounded-[16px] border bg-white px-sm py-[12px] shadow-xs tablet-lg:flex tablet-lg:flex-col tablet-lg:gap-xs" data-sentry-component="BuildQuizFloatingButtons" data-sentry-source-file="BuildQuizFloatingButtons.tsx">
      <div className="flex flex-col gap-4xs">
        <FloatingButton onClick={() => handleCreateQuiz('multiple')} tooltipContent={t('build.quizType.multiple')} defaultIcon={<CheckSmallCircleIcon size={'md'} />} data-sentry-element="FloatingButton" data-sentry-source-file="BuildQuizFloatingButtons.tsx" />
        <FloatingButton onClick={() => handleCreateQuiz('subjective')} tooltipContent={t('build.quizType.subjective')} defaultIcon={<MessageLinesIcon size={'md'} />} data-sentry-element="FloatingButton" data-sentry-source-file="BuildQuizFloatingButtons.tsx" />

        <FloatingButton onClick={() => handleCreateQuiz('ox')} tooltipContent={t('build.quizType.ox')} defaultIcon={<LocalizedOxTypeIcon size="md" />} data-sentry-element="FloatingButton" data-sentry-source-file="BuildQuizFloatingButtons.tsx" />
        <FloatingButton onClick={() => handleCreateQuiz('essay')} tooltipContent={t('build:build.quizType.essay')} defaultIcon={<TextLongLeftIcon size="md" />} data-sentry-element="FloatingButton" data-sentry-source-file="BuildQuizFloatingButtons.tsx" />
        <WithPlanBadge isBadgeVisible={true} planType={featurePlanType} className="pointer-events-none right-[-4px] top-[-5px] size-[18px] translate-x-0 translate-y-0" glyph={<BoltFillIcon width={12} height={12} fill={ICON_COLOR.inverse} aria-hidden />} data-sentry-element="WithPlanBadge" data-sentry-source-file="BuildQuizFloatingButtons.tsx">
          <FloatingButton onClick={() => upsellingGuard(() => handleCreateQuiz('match'), {
          feature: 'MATCH',
          from: 'match'
        })} tooltipContent={t('common:quizCard.typeBadge.match')} defaultIcon={<SquareGridSemiFillIcon size="md" />} data-sentry-element="FloatingButton" data-sentry-source-file="BuildQuizFloatingButtons.tsx" />
        </WithPlanBadge>
        <WithPlanBadge isBadgeVisible={true} planType={orderFeaturePlanType} className="pointer-events-none right-[-4px] top-[-5px] size-[18px] translate-x-0 translate-y-0" glyph={<BoltFillIcon width={12} height={12} fill={ICON_COLOR.inverse} aria-hidden />} data-sentry-element="WithPlanBadge" data-sentry-source-file="BuildQuizFloatingButtons.tsx">
          <FloatingButton onClick={() => upsellingGuard(() => handleCreateQuiz('order'), {
          feature: 'ORDER',
          from: 'order'
        })} tooltipContent={t('common:quizCard.typeBadge.order')} defaultIcon={<OrderIcon size="md" />} data-sentry-element="FloatingButton" data-sentry-source-file="BuildQuizFloatingButtons.tsx" />
        </WithPlanBadge>
      </div>

      {shouldRenderAiEntry && <motion.div className="flex flex-col gap-xs" initial={false} animate={{
      opacity: aiEntryTransitionState === 'exiting' ? 0 : 1,
      x: aiEntryTransitionState === 'exiting' ? 8 : 0
    }} transition={{
      duration: 0.16,
      ease: 'easeIn'
    }}>
          <div className="mx-auto h-[1px] w-xl border-y-[0.5px] border-alternative " />
          <FloatingAiBuildButton />
        </motion.div>}
    </div>;
};
const FloatingAiBuildButton = () => {
  const {
    t
  } = useTranslation(['build']);
  const openAiBuilderChat = useOpenAiBuilderChat();
  return <FloatingButton onClick={() => openAiBuilderChat({
    entry: 'chat',
    source: 'floating_ai_build_button'
  })} tooltipContent={t('build:build.ai.createQuiz')} defaultIcon={<MagicwandFillIcon size="md" fill={ICON_COLOR.default} />} data-sentry-element="FloatingButton" data-sentry-component="FloatingAiBuildButton" data-sentry-source-file="BuildQuizFloatingButtons.tsx" />;
};
const FloatingButton = ({
  onClick,
  defaultIcon,
  tooltipContent
}: {
  onClick: () => void;
  defaultIcon: React.JSX.Element;
  tooltipContent: string;
}) => {
  return <Tooltip side="right" arrow={{
    hide: true
  }} trigger={<IconButton variant={'transparent'} intent={'neutral'} size={'md'} onMouseDown={e => {
    e.stopPropagation();
    e.preventDefault();
  }} onClick={e => {
    e.stopPropagation();
    onClick();
  }}>
          {defaultIcon}
        </IconButton>} content={tooltipContent} data-sentry-element="Tooltip" data-sentry-component="FloatingButton" data-sentry-source-file="BuildQuizFloatingButtons.tsx" />;
};